import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Check, Copy, Key, Loader2, Radio } from 'lucide-react';

import { companiesAPI, organizationsAPI, trucksAPI } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { buildGpsConfigPayload, gpsSecretHint, hasGpsSecret } from '../../lib/gpsDevice';
import { LEXIQUE } from '../../lib/lexique';
import { libelle } from '../../lib/libelles';
import { messageErreur } from '../../lib/erreurs';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { ChampsAvances } from '../ui/champs-avances';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from '../ui/sonner';

/**
 * Le formulaire camion, en un seul exemplaire.
 *
 * Il existait deux fois dans `TrucksPage` (création et modification), plus un
 * troisième remplissage à la main depuis le tiroir de détails qui oubliait le
 * GPS: rouvrir la fiche par ce chemin puis enregistrer effaçait le boîtier.
 * `truckToForm` est désormais la seule façon de passer d'un camion à un
 * formulaire.
 */

// Numéros physiques partagés entre sociétés. Les trois premiers et l'état
// technique ne se changent que par la société créatrice; les autres peuvent
// être *complétés* par toute société membre tant qu'ils sont vides.
export const UNIVERSAL_LOCKED_FIELDS = ['matricule', 'capacity_liters', 'technical_status'];
export const UNIVERSAL_FILLABLE_FIELDS = ['tank_number', 'tractor_vin', 'tank_vin', 'brand', 'year'];

export const TRUCK_FIELD_LABELS = {
  matricule: LEXIQUE.plaqueTracteur.toLowerCase(),
  tank_number: LEXIQUE.plaqueCiterne.toLowerCase(),
  tractor_vin: LEXIQUE.chassisTracteur.toLowerCase(),
  tank_vin: LEXIQUE.chassisCiterne.toLowerCase(),
  brand: 'marque',
  year: 'année',
  capacity_liters: 'capacité',
  technical_status: 'état technique',
};

export const emptyTruckForm = () => ({
  matricule: '',
  capacity_liters: '',
  tank_number: '',
  tractor_vin: '',
  tank_vin: '',
  brand: '',
  year: '',
  company_id: '',
  societe_select: '', // encodé: '', 'me', 'org:UUID', 'company:UUID'
  driver_full_name: '',
  driver_phone: '',
  technical_status: 'OK',
  current_status: 'FREE',
  ownership_type: 'PROPRE',
  owner_name: '',
  owner_phone: '',
  _auto_owner_name: '',
  tracking_mode: 'hybrid',
  gps_device_enabled: false,
  gps_provider: '',
  gps_device_id: '',
  gps_imei: '',
  gps_shared_secret: '',
  gps_label: '',
  gps_config_json: {},
  gps_status: 'inactive',
  gps_notes: '',
});

/** Un camion enregistré → les valeurs du formulaire. `null` → formulaire vide. */
export const truckToForm = (truck, { societeSelect = '' } = {}) => {
  if (!truck) return emptyTruckForm();
  const hasExternalCompany = !!(truck.company_id || truck.company_name);
  const storedOwnership = truck.ownership_type || 'PROPRE';
  // Un ancien camion rattaché à une société externe mais resté « PROPRE »
  // s'affiche « LOUE »: c'est ce qu'il est.
  const effectiveOwnership = hasExternalCompany ? 'LOUE' : storedOwnership;
  return {
    ...emptyTruckForm(),
    matricule: truck.matricule || '',
    capacity_liters: truck.capacity_liters ?? '',
    tank_number: truck.tank_number || '',
    tractor_vin: truck.tractor_vin || '',
    tank_vin: truck.tank_vin || '',
    brand: truck.brand || '',
    year: truck.year ?? '',
    company_id: truck.company_id || '',
    societe_select: societeSelect,
    driver_full_name: truck.driver_full_name || '',
    driver_phone: truck.driver_phone || '',
    technical_status: truck.technical_status || 'OK',
    current_status: truck.current_status || 'FREE',
    ownership_type: effectiveOwnership,
    owner_name: truck.owner_name || (hasExternalCompany ? truck.company_name || '' : ''),
    owner_phone: truck.owner_phone || '',
    _auto_owner_name: hasExternalCompany ? truck.company_name || '' : '',
    tracking_mode: truck.tracking_mode || 'hybrid',
    gps_device_enabled: !!truck.gps_device_enabled,
    gps_provider: truck.gps_provider || '',
    gps_device_id: truck.gps_device_id || '',
    gps_imei: truck.gps_imei || '',
    // Jamais le secret lui-même: on dit seulement qu'il est posé.
    gps_shared_secret: '',
    gps_label: truck.gps_label || '',
    gps_config_json: truck.gps_config_json || {},
    gps_status: truck.gps_status || (truck.gps_device_enabled ? 'configured' : 'inactive'),
    gps_notes: truck.gps_notes || '',
  };
};

const isBlank = (value) => value === '' || value === null || value === undefined;

const sameValue = (field, a, b) => {
  if (isBlank(a) && isBlank(b)) return true;
  if (field === 'capacity_liters' || field === 'year') return Number(a) === Number(b);
  return String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();
};

/**
 * Le message d'après enregistrement: ce que le serveur a ignoré (numéros
 * physiques déjà remplis par la société créatrice) et ses avertissements
 * (doublon de plaque citerne ou de châssis). Rien ne doit passer pour
 * enregistré alors qu'il ne l'a pas été.
 */
export const describeTruckSave = (response) => {
  const ignored = (response?.universal_fields_ignored || []).map(
    (field) => TRUCK_FIELD_LABELS[field] || 'autre information',
  );
  const warnings = (response?.warnings || [])
    .map((warning) =>
      typeof warning === 'string' ? warning : warning?.message || warning?.detail || '',
    )
    .filter(Boolean);
  return { ignored, warnings };
};

/** Champs du formulaire (création et modification). */
export function TruckFormFields({
  formData,
  setFormData,
  mode = 'create',
  truck = null,
  canEditUniversal = true,
  submitAttempted = false,
  societeOptions = [],
  onSocieteChange,
}) {
  const isCreate = mode === 'create';
  const set = (field) => (event) =>
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  const setUpper = (field) => (event) =>
    setFormData((prev) => ({ ...prev, [field]: event.target.value.toUpperCase() }));

  // Un numéro physique encore vide peut être complété par toute société
  // membre; déjà rempli, il appartient à la société créatrice.
  const fillableDisabled = (field) => !canEditUniversal && !isBlank(truck?.[field]);

  // Ce qui décide si le dépliant s'ouvre tout seul. `tracking_mode` en est
  // volontairement absent: il vaut 'hybrid' par défaut et ouvrirait le bloc à
  // chaque fiche. Le secret partagé non plus: il n'est jamais relu du serveur.
  const valeurs = [
    formData.tank_number,
    formData.tractor_vin,
    formData.tank_vin,
    formData.brand,
    formData.year,
    formData.gps_device_enabled,
    formData.gps_provider,
    formData.gps_device_id,
    formData.gps_imei,
    formData.gps_label,
    ...(isCreate ? [] : [formData.owner_name, formData.owner_phone]),
  ];
  const showOwner = !isCreate && formData.ownership_type === 'LOUE';
  // plaque citerne, 2 châssis, marque, année, 7 réglages GPS (+ propriétaire)
  const nombre = 12 + (showOwner ? 2 : 0);

  return (
    <div className="space-y-4 py-4">
      {!isCreate && !canEditUniversal && (
        <div className="flex items-center gap-1 rounded-md border border-flame/30 bg-flame/10 px-3 py-2 text-xs text-flame-ink">
          Caractéristiques physiques gérées par l’organisation propriétaire.
          <Aide titre="Caractéristiques physiques">
            Les caractéristiques physiques (plaques, châssis, capacité, marque, année, état
            technique) sont gérées par l’organisation propriétaire. Vous pouvez compléter
            celles qui sont encore vides et modifier les champs liés à votre flotte.
          </Aide>
        </div>
      )}

      <div>
        <Label className="label-base">{LEXIQUE.plaqueTracteur} *</Label>
        <Input
          placeholder={isCreate ? 'Ex: SN-1234-AA' : 'Ex: BJ8652MD'}
          value={formData.matricule}
          onChange={setUpper('matricule')}
          className="input-base font-mono"
          disabled={!isCreate && !canEditUniversal}
          data-testid={isCreate ? 'new-truck-matricule' : 'edit-truck-matricule'}
        />
        <FieldError show={submitAttempted && !formData.matricule}>
          Plaque tracteur requise
        </FieldError>
      </div>

      <div>
        <Label className="label-base">Société *</Label>
        <SearchableSelect
          className="input-base"
          data-testid={isCreate ? 'new-truck-company' : 'edit-truck-company'}
          placeholder="Sélectionner une société"
          searchPlaceholder="Rechercher une société…"
          emptyMessage="Aucune société trouvée"
          value={formData.societe_select || ''}
          onValueChange={onSocieteChange}
          options={societeOptions}
        />
        <FieldError show={submitAttempted && !formData.societe_select}>
          Société requise
        </FieldError>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="label-base">Chauffeur (nom)</Label>
          <Input
            placeholder="Nom du chauffeur"
            value={formData.driver_full_name}
            onChange={set('driver_full_name')}
            className="input-base"
            data-testid={isCreate ? 'new-truck-driver-name' : undefined}
          />
          <FieldError
            show={submitAttempted && !!formData.driver_phone && !formData.driver_full_name}
          >
            Nom requis
          </FieldError>
        </div>
        <div>
          <Label className="label-base">Téléphone chauffeur</Label>
          <Input
            placeholder="Ex: 77 000 00 00"
            value={formData.driver_phone}
            onChange={set('driver_phone')}
            className="input-base"
            data-testid={isCreate ? 'new-truck-driver-phone' : undefined}
          />
        </div>
      </div>

      <div>
        <Label className="label-base">Capacité (L) *</Label>
        <Input
          type="number"
          placeholder="30000"
          value={formData.capacity_liters}
          onChange={set('capacity_liters')}
          className="input-base"
          disabled={!isCreate && !canEditUniversal}
          data-testid={isCreate ? 'new-truck-capacity' : undefined}
        />
        <FieldError show={submitAttempted && !formData.capacity_liters}>
          Capacité requise
        </FieldError>
      </div>

      {/* « État technique » n'est pas demandé à la création: un camion qu'on
          enregistre est en état de rouler. Le serveur pose OK. */}
      {!isCreate && (
        <>
          <div>
            <Label className="label-base">État technique</Label>
            <Select
              value={formData.technical_status}
              onValueChange={(v) => setFormData((prev) => ({ ...prev, technical_status: v }))}
              disabled={!canEditUniversal}
            >
              <SelectTrigger className="input-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OK">En bon état</SelectItem>
                <SelectItem value="MAINTENANCE">En maintenance</SelectItem>
                <SelectItem value="BROKEN">En panne</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Le statut se lit, il ne se choisit pas: `TruckAvailabilityService`
              le recalcule à partir de la maintenance, des locations et des
              trajets en cours. Pour immobiliser, c'est le bouton de la fiche. */}
          <div>
            <div className="flex items-center gap-1">
              <Label className="label-base">Statut</Label>
              <Aide titre="Statut du camion">
                Calculé automatiquement d’après la maintenance, les locations et les trajets
                en cours.
              </Aide>
            </div>
            <p className="input-base flex items-center bg-muted/50 text-muted-foreground">
              {libelle('truckStatus', truck?.current_status || 'FREE')}
            </p>
          </div>

          <div>
            <div className="flex items-center gap-1">
              <Label className="label-base">Type de propriété</Label>
              {formData.ownership_type === 'LOUE' && (
                <Aide titre="Camion loué">
                  Le nom et le téléphone du propriétaire se saisissent dans « Plus de
                  détails », ci-dessous.
                </Aide>
              )}
            </div>
            <Select
              value={formData.ownership_type}
              onValueChange={(v) => setFormData((prev) => ({ ...prev, ownership_type: v }))}
            >
              <SelectTrigger className="input-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PROPRE">Camion propre</SelectItem>
                <SelectItem value="LOUE">Camion loué</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <ChampsAvances
        consequence="Sans ces réglages, le camion n’apparaît pas sur la carte."
        nombre={nombre}
        valeurs={valeurs}
      >
        <div>
          <div className="flex items-center gap-1">
            <Label className="label-base">Immatriculation citerne</Label>
            <Aide titre="Immatriculation citerne">
              La plaque portée par la citerne quand elle diffère de celle du tracteur.
            </Aide>
          </div>
          <Input
            placeholder="Ex: SN-5678-BB"
            value={formData.tank_number}
            onChange={setUpper('tank_number')}
            className="input-base font-mono"
            disabled={!isCreate && fillableDisabled('tank_number')}
            data-testid={isCreate ? 'new-truck-tank-number' : 'edit-truck-tank-number'}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label className="label-base">{LEXIQUE.chassisTracteur}</Label>
            <Input
              value={formData.tractor_vin}
              onChange={setUpper('tractor_vin')}
              className="input-base font-mono"
              disabled={!isCreate && fillableDisabled('tractor_vin')}
              data-testid="truck-form-tractor-vin"
            />
          </div>
          <div>
            <Label className="label-base">{LEXIQUE.chassisCiterne}</Label>
            <Input
              value={formData.tank_vin}
              onChange={setUpper('tank_vin')}
              className="input-base font-mono"
              disabled={!isCreate && fillableDisabled('tank_vin')}
              data-testid="truck-form-tank-vin"
            />
          </div>
          <div>
            <Label className="label-base">Marque</Label>
            <Input
              placeholder="Ex: Renault"
              value={formData.brand}
              onChange={set('brand')}
              className="input-base"
              disabled={!isCreate && fillableDisabled('brand')}
            />
          </div>
          <div>
            <Label className="label-base">Année</Label>
            <Input
              type="number"
              placeholder="Ex: 2018"
              value={formData.year}
              onChange={set('year')}
              className="input-base"
              disabled={!isCreate && fillableDisabled('year')}
            />
          </div>
        </div>

        <GpsFields
          formData={formData}
          setFormData={setFormData}
          truck={truck}
          canEditUniversal={isCreate || canEditUniversal}
        />

        {showOwner && (
          <>
            <div>
              <Label className="label-base">Nom du propriétaire</Label>
              <Input
                className="input-base"
                value={formData.owner_name}
                onChange={set('owner_name')}
                placeholder="Nom de l'entreprise de location"
              />
            </div>
            <div>
              <Label className="label-base">Téléphone propriétaire</Label>
              <Input
                className="input-base"
                value={formData.owner_phone}
                onChange={set('owner_phone')}
                placeholder="Contact du propriétaire"
              />
            </div>
          </>
        )}
      </ChampsAvances>
    </div>
  );
}

function GpsFields({ formData, setFormData, truck, canEditUniversal }) {
  const set = (field) => (event) =>
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  const off = !canEditUniversal || !formData.gps_device_enabled;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-muted-foreground" />
          <Label className="label-base mb-0">Boîtier GPS</Label>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
          <input
            type="checkbox"
            checked={!!formData.gps_device_enabled}
            disabled={!canEditUniversal}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                gps_device_enabled: e.target.checked,
                gps_status: e.target.checked ? 'configured' : 'inactive',
              }))
            }
          />
          Activer
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="label-base">{LEXIQUE.modeSuivi}</Label>
          <Select
            value={formData.tracking_mode}
            disabled={!canEditUniversal}
            onValueChange={(v) => setFormData((prev) => ({ ...prev, tracking_mode: v }))}
          >
            <SelectTrigger className="input-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hybrid">Mixte (boîtier et application)</SelectItem>
              <SelectItem value="hardware">Boîtier GPS</SelectItem>
              <SelectItem value="driver_app">Application du chauffeur</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-base">{LEXIQUE.fournisseurGps}</Label>
          <Input
            className="input-base"
            disabled={off}
            value={formData.gps_provider}
            placeholder="Ex : Traccar"
            onChange={set('gps_provider')}
          />
        </div>
        <div>
          <Label className="label-base">{LEXIQUE.identifiantBoitier}</Label>
          <Input
            className="input-base font-mono"
            disabled={off}
            value={formData.gps_device_id}
            onChange={set('gps_device_id')}
          />
        </div>
        <div>
          <Label className="label-base">Numéro IMEI du boîtier</Label>
          <Input
            className="input-base font-mono"
            disabled={off}
            value={formData.gps_imei}
            onChange={set('gps_imei')}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="label-base">
            Code secret du boîtier
            {hasGpsSecret(truck) ? ' (déjà enregistré)' : ''}
          </Label>
          <Input
            type="password"
            autoComplete="new-password"
            className="input-base font-mono"
            disabled={off}
            value={formData.gps_shared_secret}
            placeholder={
              hasGpsSecret(truck) ? '••••••••' : 'Collez le code réglé dans le boîtier'
            }
            onChange={set('gps_shared_secret')}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {gpsSecretHint(truck)} Ce même code doit être réglé dans le boîtier.
          </p>
        </div>
      </div>
      <div>
        <Label className="label-base">Note sur le boîtier</Label>
        <Input
          className="input-base"
          disabled={off}
          value={formData.gps_label || formData.gps_notes}
          placeholder="Ex : boîtier dans la cabine"
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              gps_label: e.target.value,
              gps_notes: e.target.value,
            }))
          }
        />
      </div>
    </div>
  );
}

/**
 * Création (`truck` absent) ou modification d'un camion.
 *
 * `onSaved(savedTruck, { created })` est appelé après succès; le dialogue se
 * ferme de lui-même et affiche le code d'accès chauffeur s'il y en a un.
 */
export default function TruckFormDialog({ open, onOpenChange, truck = null, onSaved }) {
  const { user, isSuperAdmin } = useAuth();
  const mode = truck ? 'edit' : 'create';
  const [formData, setFormData] = useState(emptyTruckForm);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [referencesLoaded, setReferencesLoaded] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [accessCode, setAccessCode] = useState(null); // {code, name}
  const [copied, setCopied] = useState(false);
  // Remonte les champs à chaque (ré)initialisation: « Plus de détails » ne
  // décide de s'ouvrir qu'au montage, d'après les valeurs déjà remplies.
  const [formVersion, setFormVersion] = useState(0);

  const myOrgId = user?.org_id || user?.organization_id;
  const canEditUniversal = isSuperAdmin || !truck || truck?.is_creator !== false; // null → créateur (ancien)

  const loadCompanies = useCallback(async () => {
    try {
      const { items } = await fetchAllPages((params) => companiesAPI.getAllPaged(params));
      setCompanies(items);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  }, []);

  const loadOrganizations = useCallback(async () => {
    try {
      const result = await organizationsAPI.getEvery();
      setOrganizations(Array.isArray(result) ? result : result?.data || []);
    } catch (err) {
      // Non bloquant: un utilisateur LOGISTIQUE peut ne pas y avoir accès.
      console.warn('Failed to load organizations:', err?.response?.status);
      setOrganizations([]);
    }
  }, []);

  // Les listes de sociétés ne sont chargées qu'à la première ouverture: la
  // fiche camion monte ce dialogue sans qu'on l'ouvre forcément.
  useEffect(() => {
    if (!open || referencesLoaded) return;
    setReferencesLoaded(true);
    loadCompanies();
    loadOrganizations();
  }, [open, referencesLoaded, loadCompanies, loadOrganizations]);

  const deriveSocieteSelect = useCallback(
    (row) => {
      if (!row) return '';
      if (row.company_id) return `company:${row.company_id}`;
      if (myOrgId && row.organization_id === myOrgId && !row.company_name) return 'me';
      if (row.company_name) {
        const match = organizations.find(
          (o) => (o.name || '').toLowerCase() === String(row.company_name).toLowerCase(),
        );
        if (match) return `org:${match.id}`;
      }
      return '';
    },
    [myOrgId, organizations],
  );

  // (Ré)initialise le formulaire à chaque ouverture.
  useEffect(() => {
    if (!open) return;
    setSubmitAttempted(false);
    setFormData(truckToForm(truck, { societeSelect: deriveSocieteSelect(truck) }));
    setFormVersion((version) => version + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, truck]);

  // Les organisations arrivent parfois après l'ouverture: on complète la
  // société si elle n'a pas pu être déduite plus tôt.
  useEffect(() => {
    if (!open || !truck) return;
    setFormData((prev) =>
      prev.societe_select ? prev : { ...prev, societe_select: deriveSocieteSelect(truck) },
    );
  }, [open, truck, deriveSocieteSelect]);

  const myOrgName = organizations.find((o) => o.id === myOrgId)?.name || 'Mon organisation';
  const societeOptions = useMemo(
    () => [
      { value: 'me', label: myOrgName, description: 'Mon entreprise' },
      ...organizations
        .filter((o) => o.id !== myOrgId)
        .map((o) => ({
          value: `org:${o.id}`,
          label: o.name || 'Organisation sans nom',
          description: 'Autres organisations',
        })),
      ...companies.map((c) => ({
        value: `company:${c.id}`,
        label: c.name || 'Société sans nom',
        description: 'Mes sociétés',
      })),
      {
        value: '__add_new__',
        label: '+ Ajouter une nouvelle société',
        pinned: true,
        className: 'text-flame font-semibold',
      },
    ],
    [myOrgName, myOrgId, organizations, companies],
  );

  const resolveSocietePayload = (sel) => {
    if (!sel || sel === 'me') return { company_id: null, company_name: null };
    if (sel.startsWith('org:')) {
      const org = organizations.find((o) => o.id === sel.slice(4));
      return { company_id: null, company_name: org?.name || null };
    }
    if (sel.startsWith('company:')) {
      const id = sel.slice(8);
      const company = companies.find((c) => c.id === id);
      return { company_id: id, company_name: company?.name || null };
    }
    return { company_id: null, company_name: null };
  };

  const handleSocieteChange = (value) => {
    if (value === '__add_new__') {
      setNewCompanyName('');
      setShowAddCompany(true);
      return;
    }
    setFormData((prev) => {
      const next = { ...prev, societe_select: value };
      const { company_name } = resolveSocietePayload(value);
      if (value && value !== 'me') {
        // Société externe choisie → le camion lui est loué.
        next.ownership_type = 'LOUE';
        if (!prev.owner_name || prev.owner_name === prev._auto_owner_name) {
          next.owner_name = company_name || '';
          next._auto_owner_name = company_name || '';
        }
      } else if (value === 'me') {
        if (prev.ownership_type === 'LOUE' && prev.owner_name === prev._auto_owner_name) {
          next.ownership_type = 'PROPRE';
          next.owner_name = '';
          next.owner_phone = '';
          next._auto_owner_name = '';
        }
      }
      return next;
    });
  };

  const handleCreateCompany = async () => {
    const name = newCompanyName.trim();
    if (!name) return;
    try {
      const created = await companiesAPI.create({ name });
      const newId = created?.id || created?.data?.id;
      if (newId) {
        await loadCompanies();
        setFormData((prev) => ({ ...prev, societe_select: `company:${newId}` }));
        toast.success('Société ajoutée');
      }
      setShowAddCompany(false);
      setNewCompanyName('');
    } catch (err) {
      console.error('Failed to create company:', err);
      toast.error(messageErreur(err, 'La société n’a pas pu être ajoutée.'));
    }
  };

  const buildPayload = () => {
    const { company_id, company_name } = resolveSocietePayload(formData.societe_select);
    const payload = {
      ...formData,
      capacity_liters: Number(formData.capacity_liters),
      year: isBlank(formData.year) ? null : Number(formData.year),
      company_id,
      company_name,
    };
    delete payload.societe_select;
    delete payload.gps_shared_secret;
    delete payload._auto_owner_name;
    ['tank_number', 'tractor_vin', 'tank_vin', 'brand'].forEach((field) => {
      payload[field] = String(payload[field] || '').trim() || null;
    });
    return payload;
  };

  const validate = () => {
    setSubmitAttempted(true);
    return !!(formData.matricule && formData.capacity_liters && formData.societe_select);
  };

  const showAccessCode = (saved) => {
    if (saved?.driver_access_code_once) {
      setAccessCode({
        code: saved.driver_access_code_once,
        name:
          saved.default_driver_full_name ||
          saved.driver_full_name ||
          formData.driver_full_name ||
          'Chauffeur',
      });
    }
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      // L'état technique n'est pas demandé à la création: le serveur pose OK.
      delete payload.technical_status;
      ['tank_number', 'tractor_vin', 'tank_vin', 'brand', 'year'].forEach((field) => {
        if (payload[field] === null) delete payload[field];
      });
      const gpsConfig = buildGpsConfigPayload(formData.gps_shared_secret);
      if (gpsConfig) payload.gps_config_json = gpsConfig;
      else delete payload.gps_config_json;
      const created = await trucksAPI.create(payload);
      onOpenChange(false);
      if (created?.joined) {
        toast.info(
          "Ce camion existait déjà — ajouté à votre flotte. Les caractéristiques physiques sont définies par l'organisation propriétaire.",
        );
      } else {
        toast.success('Camion créé');
      }
      const { warnings } = describeTruckSave(created);
      if (warnings.length) toast.warning(warnings.join(' · '));
      showAccessCode(created);
      if (onSaved) await onSaved(created, { created: true });
    } catch (err) {
      console.error('Failed to create truck:', err);
      toast.error(messageErreur(err, 'Le camion n’a pas pu être créé.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!truck?.id || !validate()) return;
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      // Le statut n'est pas saisissable: renvoyer la valeur lue à l'ouverture
      // remettrait « libre » un camion parti entre-temps.
      delete payload.current_status;
      // Les numéros physiques inchangés ne partent pas: une société non
      // créatrice se verrait sinon signaler comme « ignorés » des champs
      // qu'elle n'a pas touchés.
      [...UNIVERSAL_LOCKED_FIELDS, ...UNIVERSAL_FILLABLE_FIELDS].forEach((field) => {
        if (sameValue(field, payload[field], truck[field])) delete payload[field];
      });
      // Le boîtier GPS est universel aussi, et ses champs sont désactivés pour
      // une société non créatrice: les renvoyer (un « inactive » par défaut
      // contre un NULL en base) déclenchait un faux « non modifiés ».
      if (!canEditUniversal) {
        Object.keys(payload).forEach((field) => {
          if (field === 'tracking_mode' || field.startsWith('gps_')) delete payload[field];
        });
      }
      // Absent du payload = inchangé côté serveur. Un objet vide effacerait le
      // secret d'un camion dont on venait corriger la plaque.
      const gpsConfig = buildGpsConfigPayload(formData.gps_shared_secret, truck);
      if (gpsConfig) payload.gps_config_json = gpsConfig;
      else delete payload.gps_config_json;
      const updated = await trucksAPI.update(truck.id, payload);
      onOpenChange(false);
      const { ignored, warnings } = describeTruckSave(updated);
      if (ignored.length) {
        toast.warning('Camion mis à jour, sauf certains numéros', {
          description: `Non modifiés (gérés par la société propriétaire) : ${ignored.join(', ')}.`,
        });
      } else {
        toast.success('Camion mis à jour');
      }
      if (warnings.length) toast.warning(warnings.join(' · '));
      showAccessCode(updated);
      if (onSaved) await onSaved(updated, { created: false });
    } catch (err) {
      console.error('Failed to update truck:', err);
      toast.error(messageErreur(err, 'Le camion n’a pas pu être modifié.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const incomplete = !formData.matricule || !formData.capacity_liters || !formData.societe_select;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? 'Nouveau camion' : 'Modifier le camion'}</DialogTitle>
          </DialogHeader>

          <TruckFormFields
            key={formVersion}
            formData={formData}
            setFormData={setFormData}
            mode={mode}
            truck={truck}
            canEditUniversal={canEditUniversal}
            submitAttempted={submitAttempted}
            societeOptions={societeOptions}
            onSocieteChange={handleSocieteChange}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={mode === 'create' ? handleCreate : handleUpdate}
              disabled={isSubmitting || incomplete}
              data-testid={mode === 'create' ? 'submit-new-truck' : 'submit-edit-truck'}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : mode === 'create' ? (
                'Créer'
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Code d'accès du chauffeur créé depuis le formulaire camion. */}
      <Dialog open={!!accessCode} onOpenChange={(next) => !next && setAccessCode(null)}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-flame" />
              Code d'accès chauffeur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Communiquez ce code à <strong>{accessCode?.name}</strong> pour la connexion à
              l'application mobile. Il ne sera plus visible en clair après la fermeture.
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <code className="flex-1 text-center font-mono text-2xl font-bold tracking-widest text-flame">
                {accessCode?.code}
              </code>
              <Button type="button" variant="outline" size="sm" onClick={() => copyCode(accessCode?.code)}>
                {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button className="btn-primary" onClick={() => setAccessCode(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nouvelle société, sans quitter le formulaire. */}
      <Dialog
        open={showAddCompany}
        onOpenChange={(next) => {
          if (!next) {
            setShowAddCompany(false);
            setNewCompanyName('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-flame" />
              Nouvelle société
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <p className="text-sm text-muted-foreground">
              Cette société sera visible uniquement dans votre espace.
            </p>
            <div>
              <Label className="label-base">Nom de la société *</Label>
              <Input
                placeholder="Ex: Transport ABC"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                className="input-base"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddCompany(false);
                setNewCompanyName('');
              }}
            >
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleCreateCompany}
              disabled={!newCompanyName.trim()}
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
